// ==========================================
// DATE CALCULATION HELPERS
// ==========================================

export const getDaysBetweenDates = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(start).getTime();
  const endDate = new Date(end).getTime();
  return Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
};

export const calculateDueDate = (invoice) => {
  if (!invoice.created_at || !invoice.payment_term) return null;
  const originDate = new Date(invoice.created_at);
  const dueDate = new Date(originDate);
  dueDate.setDate(originDate.getDate() + invoice.payment_term);
  return dueDate;
};

export const getDaysLate = (invoice) => {
  if (!invoice.payment_date) return "N/A";
  const calculatedDueDate = calculateDueDate(invoice);
  if (!calculatedDueDate) return "N/A";
  
  const paymentDate = new Date(invoice.payment_date);
  const daysLate = getDaysBetweenDates(calculatedDueDate, paymentDate);
  return daysLate < 0 ? 0 : daysLate;
};

export const getTotalDaysToPay = (invoice) => {
  if (!invoice.payment_date) return "N/A";
  return getDaysBetweenDates(invoice.created_at, invoice.payment_date);
};

// ==========================================
// DATE FORMATTING
// ==========================================

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
};

// ==========================================
// STATUS UTILITIES
// ==========================================

export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid': return 'success';
    case 'approved': return 'info';
    case 'pending': return 'warning';
    case 'cancelled': return 'error';
    case 'overdue': return 'error';
    case 'draft': return 'default';
    default: return 'default';
  }
};

export const getStatusText = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid': return 'Paid';
    case 'approved': return 'Approved';
    case 'pending': return 'Pending Payment';
    case 'cancelled': return 'Cancelled';
    case 'overdue': return 'Overdue';
    case 'draft': return 'Draft';
    default: return status || 'Unknown';
  }
};

// ==========================================
// EFFECTIVE VALUE RESOLUTION
// ==========================================
/**
 * PRIORITY ORDER FOR CLIENT INFORMATION (BILL TO):
 * 1. Invoice direct fields (invoice.client_name, invoice.client_email, etc.)
 * 2. Invoice custom_details (custom overrides for this specific invoice)
 * 3. Fallback values (user info or 'N/A')
 * 
 * NOTE: GlobalUserInvoiceSettings should NOT be used for client/bill-to information
 * 
 * PRIORITY ORDER FOR COMPANY INFORMATION (BILL FROM):
 * See getCompanyInfo() function below
 */

export const getEffectiveValue = (invoiceData, invoiceSettings, customKey, fallbackValue) => {
  const invoice = invoiceData?.invoice;
  const user = invoiceData?.user;
  
  // ====================================
  // PRIORITY 1: Invoice Direct Fields
  // ====================================
  let invoiceFieldValue = null;
  
  switch(customKey) {
    case 'billed_to':
      // CLIENT INFORMATION - Only from invoice data, never from settings
      invoiceFieldValue = invoice?.client_name || invoice?.billed_to;
      break;
      
    case 'invoice_number':
      invoiceFieldValue = invoice?.po_number || invoice?.invoice_number || invoice?.id;
      break;
      
    case 'date_of_issue':
      invoiceFieldValue = invoice?.created_at || invoice?.invoice_created_at;
      break;
      
    default:
      // For any other field, check if it exists directly on invoice
      invoiceFieldValue = invoice?.[customKey];
  }
  
  if (invoiceFieldValue !== null && invoiceFieldValue !== undefined && invoiceFieldValue !== '') {
    return invoiceFieldValue;
  }
  
  // ====================================
  // PRIORITY 2: Invoice Custom Details
  // ====================================
  const customDetailsValue = invoice?.custom_details?.[customKey];
  if (customDetailsValue !== null && customDetailsValue !== undefined && customDetailsValue !== '') {
    return customDetailsValue;
  }
  
  // ====================================
  // PRIORITY 3: Special Fallbacks
  // ====================================
  // NOTE: We do NOT use invoiceSettings for client information
  // invoiceSettings is for COMPANY (bill from) information only
  
  if (customKey === 'billed_to') {
    // Fallback to user information for bill to
    if (user) {
      const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (userName) return userName;
    }
  }
  
  if (customKey === 'invoice_number') {
    // Check if settings has a prefix to apply to invoice ID
    if (invoiceSettings?.invoice_number_prefix && invoice?.id) {
      return `${invoiceSettings.invoice_number_prefix}-${invoice.id}`;
    }
  }
  
  // ====================================
  // PRIORITY 4: Final Fallback
  // ====================================
  return fallbackValue || 'N/A';
};

// ==========================================
// COMPANY INFO RESOLUTION (BILL FROM)
// ==========================================
/**
 * PRIORITY ORDER FOR COMPANY INFORMATION (BILL FROM):
 * 1. GlobalUserInvoiceSettings (custom branding/override settings) - prioritized for name and logo if settings exist
 * 2. Actual Company database record
 * 3. Invoice-embedded company fields (snapshot at invoice creation)
 * 4. System defaults
 */

export const getCompanyInfo = (invoiceData, invoiceSettings) => {
  const { company, invoice } = invoiceData || {};
  const apiCompanyInfo = invoiceData?.company_info || {};
  const apiBankDetails = apiCompanyInfo?.bank_details || {};
  
  // Helper to get first non-empty value
  const getFirstValid = (...values) => {
    for (const val of values) {
      if (val !== null && val !== undefined && val !== '') return val;
    }
    return null;
  };
  
  return {
    // ====================================
    // COMPANY NAME (BILL FROM)
    // Priority: GlobalUserInvoiceSettings > Company DB > Invoice snapshot > Default
    // ====================================
    name: getFirstValid(
      invoiceSettings?.invoice_name,  // Prioritized if exists
      company?.name,
      invoice?.company_name,
      'Blitz Systems Corp'
    ),
    
    // ====================================
    // COMPANY LOGO
    // Priority: GlobalUserInvoiceSettings > Company DB > Invoice snapshot
    // ====================================
    logo: getFirstValid(
      invoiceSettings?.logo_url,  // Prioritized if exists
      company?.logo,
      company?.phphref,
      invoice?.company_logo
    ),
    
    // ====================================
    // COMPANY ADDRESS
    // Priority: Company DB > GlobalUserInvoiceSettings > Default
    // ====================================
    address_line_1: getFirstValid(
      company?.address,
      company?.address_line_1,
      invoiceSettings?.address_line_1,
      '8724 Cavell Lane, Houston, TX 77055'
    ),
    
    address_line_2: getFirstValid(
      company?.address_line_2,
      invoiceSettings?.address_line_2
    ),
    
    city: getFirstValid(
      company?.city,
      invoiceSettings?.city
    ),
    
    state: getFirstValid(
      company?.state,
      invoiceSettings?.state
    ),
    
    postal_code: getFirstValid(
      company?.postal_code,
      invoiceSettings?.postal_code
    ),
    
    country: getFirstValid(
      company?.country,
      invoiceSettings?.country
    ),
    
    // ====================================
    // COMPANY CONTACT INFO
    // Priority: Company DB > Invoice snapshot > GlobalUserInvoiceSettings > Default
    // ====================================
    phone_number: getFirstValid(
      company?.phone_number,
      invoice?.company_phone,
      invoiceSettings?.phone_number
    ),
    
    email: getFirstValid(
      company?.email,
      invoice?.company_email,
      invoiceSettings?.email,
      'win@useblitz.co'
    ),
    
    website: getFirstValid(
      company?.website,
      invoice?.company_website,
      invoiceSettings?.website
    ),
    
    // ====================================
    // BANKING & TAX INFO
    // Priority: Company DB > GlobalUserInvoiceSettings
    // ====================================
    bank_name: getFirstValid(
      company?.bank_name,
      apiBankDetails?.bank_name,
      invoiceSettings?.bank_name
    ),
    
    account_number: getFirstValid(
      company?.account_number,
      apiBankDetails?.account_number,
      invoiceSettings?.account_number
    ),
    
    routing_number: getFirstValid(
      company?.routing_number,
      apiBankDetails?.routing_number,
      invoiceSettings?.routing_number
    ),

    account_holder: getFirstValid(
      apiBankDetails?.account_holder
    ),

    bank_address: getFirstValid(
      apiBankDetails?.bank_address
    ),

    swift_code: getFirstValid(
      apiBankDetails?.swift_code
    ),

    beneficiary_address: getFirstValid(
      apiBankDetails?.beneficiary_address,
      apiCompanyInfo?.address_line_1
    ),
    
    tax_id: getFirstValid(
      company?.tax_id,
      invoiceSettings?.tax_id
    ),
  };
};

// ==========================================
// INVOICE NOTES AND TERMS
// Priority: Invoice-specific > Settings defaults
// ==========================================

export const getInvoiceNotes = (invoice, invoiceSettings) => {
  // Check invoice-specific notes first (highest priority)
  if (invoice?.notes && invoice.notes.trim() !== '') {
    return invoice.notes;
  }
  
  if (invoice?.invoice_notes && invoice.invoice_notes.trim() !== '') {
    return invoice.invoice_notes;
  }
  
  // Then check user's default notes from settings
  if (invoiceSettings?.default_notes && invoiceSettings.default_notes.trim() !== '') {
    return invoiceSettings.default_notes;
  }
  
  return null;
};

export const getInvoiceTerms = (invoice, invoiceSettings) => {
  // Check invoice-specific terms first (highest priority)
  if (invoice?.terms && invoice.terms.trim() !== '') {
    return invoice.terms;
  }
  
  if (invoice?.payment_terms && invoice.payment_terms.trim() !== '') {
    return invoice.payment_terms;
  }
  
  if (invoice?.client_notes && invoice.client_notes.trim() !== '') {
    return invoice.client_notes;
  }
  
  // Then check user's default terms from settings
  if (invoiceSettings?.default_terms && invoiceSettings.default_terms.trim() !== '') {
    return invoiceSettings.default_terms;
  }
  
  return null;
};

// ==========================================
// HELPER: Check if value exists and is not empty
// ==========================================

const isValidValue = (value) => {
  return value !== null && value !== undefined && value !== '';
};

// ==========================================
// SUMMARY OF PRIORITY ORDERS
// ==========================================
/*

BILL TO (Client Information):
  1. invoice.client_name, invoice.client_email (direct invoice fields)
  2. invoice.custom_details.billed_to (custom override for this invoice)
  3. user name/email (if available)
  4. 'N/A' fallback

BILL FROM (Company Information):
  1. invoiceSettings.* (GlobalUserInvoiceSettings - custom branding, prioritized for name and logo)
  2. company.* (actual company database record)
  3. invoice.company_* (snapshot at invoice creation time)
  4. System defaults

NOTES & TERMS:
  1. invoice.notes / invoice.terms (specific to this invoice)
  2. invoiceSettings.default_notes / default_terms (user's standard terms)
  3. null (no default text)

*/

// Export all utilities
export default {
  // Date utilities
  getDaysBetweenDates,
  calculateDueDate,
  getDaysLate,
  getTotalDaysToPay,
  formatDate,
  formatDateTime,
  
  // Status utilities
  getStatusColor,
  getStatusText,
  
  // Resolution utilities
  getEffectiveValue,
  getCompanyInfo,
  getInvoiceNotes,
  getInvoiceTerms,
  
  // Helper
  isValidValue,
};
